import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import dayjs from 'dayjs';
import { Subscription } from 'rxjs';

import { ErrorTextObj } from '../../models/clux/model';
import { maxDate, minDate, validateDate } from '../../services/form.validator';
import { InvalidErrorStateMatcher } from '../../utils/errorstate-matcher';
import { MatFormField, MatLabel, MatInput, MatError } from '@angular/material/input';
import { NgxMaskDirective } from 'ngx-mask';

@Component({
  selector: 'app-date-input',
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useValue: validateDate,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useValue: maxDate,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useValue: minDate,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [MatFormField, MatLabel, MatInput, NgxMaskDirective, FormsModule, ReactiveFormsModule, MatError],
})
export class DateInputComponent implements ControlValueAccessor, OnInit, OnDestroy, OnChanges {
  public dateControl: UntypedFormControl;
  public errorTypes: string[];
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('dateFormat') public outputDateFormat = 'YYYY-MM-DD';
  @Input() public errorStateMatcher: InvalidErrorStateMatcher = null;
  @Input() public maxDate: string = '';
  @Input() public minDate: string = '';
  @Input() public disabled: boolean = false;
  @Input() public errorTextObj: ErrorTextObj = {};
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() public focus = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() public blur = new EventEmitter();
  @Output() public valueChange = new EventEmitter<string>();
  private initialDateValue = '';
  private customDateFormat = 'MMDDYYYY';
  private maskDateFormat = 'MM/DD/YYYY';
  private subscriptions = new Subscription();
  private validators: ValidatorFn[];

  public ngOnInit(): void {
    this.dateControl = new UntypedFormControl({ value: this.initialDateValue, disabled: this.disabled }, this.validators);
    this.subscriptions.add(
      this.dateControl.valueChanges.subscribe((value) => {
        this.valueChange.emit(this.getDateValue(value));
      })
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.validators = [Validators.required, validateDate(this.customDateFormat)];
    if (changes.maxDate) {
      this.validators.push(maxDate(dayjs(changes.maxDate.currentValue, this.outputDateFormat).format(this.customDateFormat), this.customDateFormat));
    }
    if (changes.minDate) {
      this.validators.push(minDate(dayjs(changes.minDate.currentValue, this.outputDateFormat).format(this.customDateFormat), this.customDateFormat));
    }
    if (this.dateControl) {
      this.dateControl.setValidators(this.validators);
      this.dateControl.updateValueAndValidity();
    }
    if (changes.disabled && this.dateControl) {
      if (changes.disabled.currentValue) {
        this.dateControl.disable();
      } else {
        this.dateControl.enable();
      }
    }
    this.errorTypes = Object.keys(this.errorTextObj);
  }

  public onInput(e: KeyboardEvent): void {
    this.propagateChange(this.getDateValue((e.target as HTMLInputElement).value.replace(/\//gi, '')));
  }

  public onFocus(): void {
    this.focus.emit();
  }

  public onBlur(): void {
    this.onTouched();
    this.blur.emit();
  }

  /**
   * This method provides appropriate error message based on types of error present on control.
   * 'Required' error takes precedence over rest of errors.
   */
  public getErrorMessage(errorType: string): { showError: boolean; errorText?: string } {
    if (this.dateControl.hasError('required') && errorType === 'required') {
      return { showError: true, errorText: this.errorTextObj['required'] };
    }
    if (this.dateControl.hasError(errorType) && !this.dateControl.hasError('required') && errorType !== 'required') {
      return { showError: true, errorText: this.errorTextObj[errorType] };
    }
    return { showError: false };
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * ControlValueAccessor interface methods
   */
  public writeValue(value: string): void {
    if (value !== undefined && value !== '') {
      this.initialDateValue = this.formatDate(value, this.outputDateFormat, this.customDateFormat);
      this.dateControl.setValue(this.initialDateValue);
    }
  }

  public propagateChange = (_: string) => {};

  public registerOnChange(fn: (_: string) => void): void {
    this.propagateChange = fn;
  }

  public onTouched = () => {};

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  // ControlValueAccessor interface methods end

  private getDateValue(value: string): string {
    if (!value) {
      return '';
    }
    if (value.length === this.customDateFormat.length) {
      return this.formatDate(value, this.customDateFormat, this.outputDateFormat);
    }
    return 'Invalid date';
  }

  private formatDate(dateString: string, fromFormat: string, toFormat: string): string {
    return dayjs(dateString, fromFormat).format(toFormat);
  }
}
